import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {firstValueFrom, from} from 'rxjs';
import { first } from 'rxjs/operators';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { filePath, SourceArcApiService, SwalService, UIExportService } from '@atmi/core';
import { SourceArchivial, SourceArchivialFilter, SourceArchivialFilterMeta } from '@atmi/core/models';
import { SettingsApiService } from '@atmi/core/services/settings-api.service';
import { IndexQueryStringType } from '@atmi/core/utils';
import { SourceArchivialFiltersComponent } from '@atmi/shared/filters/source-archivial-filters/source-archivial-filters.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sources-archivial',
  styleUrls: ['./sources-archivial.component.scss'],
  templateUrl: './sources-archivial.component.html',
})
export class SourcesArchivialComponent
  extends BaseTableFilteredComponent<SourceArchivial, SourceArchivialFilter>
  implements UiTableComponent<SourceArchivial> {

  readonly MobileFiltersComponent = SourceArchivialFiltersComponent;

  readonly fields: TableField<SourceArchivial>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Titolo'),
      key: 'titolo',
      sortable: true,
    },
    {
      header: this.translate.instant('Tipo'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'tipo',
      sortable: true,
    },
    {
      header: this.translate.instant('Evento'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'evento',
    },
    {
      header: this.translate.instant('Serie'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'serie',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      template: 'date-time',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  currentFilters: IndexQueryStringType = {};

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    sort = sort && sort.length > 0 ? sort : [{ field: 'titolo', type: 'asc' }];
    this.currentFilters = { sort, filter };
    return this.sourceArchApi.index({ page, limit, sort, filter });
  });

  readonly contextMenus: Array<TableContextMenu<SourceArchivial>> = [];

  readonly isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className ===  'DocumentoArc');

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly roleService: RoleService,
    private readonly settingsApi: SettingsApiService,
    private readonly sourceArchApi: SourceArcApiService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, SourceArchivialFilterMeta, swalService, '/archival-sources/manager');

    if (this.roleService.hasCapability('DocumentoArc', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else if (this.roleService.hasCapability('DocumentoArc', ['read', 'read+all'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Visualizza'),
      });
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('DocumentoArc', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.sourceArchApi.remove(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => firstValueFrom(this.sourceArchApi.remove(id)));

    return from(Promise.all(deleteApis));
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'DocumentoArc');
    if (success) {
      this.selectedItems = [];
    }
  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string) => {
        const selection = this.selectedItemIds.length ? this.selectedItemIds : undefined;
        const blob = await firstValueFrom(this.sourceArchApi.exportList(template, this.currentFilters, selection).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'DocumentoArc',
      `docs-arc`,
    );
  }
}
